import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface InterviewMessage {
  id: string
  content: string
  isAi: boolean
  competency?: string
  timestamp: string
}

export interface CompetencyScore {
  competency: string
  score: number
  feedback?: string
  questionsAsked: string[]
  answers: string[]
}

export interface InterviewSession {
  id: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  currentCompetency: string
  questionsAsked: number
  competencyQuestionsLimit: number
  startTime?: string
  endTime?: string
  timeRemaining?: number
  interviewDuration?: number
  interviewEndedByAi: boolean
  aiEndReason?: string
  aiFinalMessage?: string
}

export interface InterviewProgress {
  progress: number
  currentStage: string
  questionsAsked: number
  totalQuestions: number
  currentCompetency: string
  completedCompetencies: string[]
}

export interface InterviewState {
  // Session data
  session: InterviewSession | null
  messages: InterviewMessage[]
  progress: InterviewProgress | null
  scores: CompetencyScore[]
  
  // UI state
  isLoading: boolean
  isConnected: boolean
  isRecording: boolean
  isTranscribing: boolean
  currentTranscription: string
  
  // Input mode
  inputMode: 'text' | 'voice'
  
  // Error handling
  error: string | null
  
  // Real-time features
  isTyping: boolean
  lastActivity: number
}

const initialState: InterviewState = {
  session: null,
  messages: [],
  progress: null,
  scores: [],
  
  isLoading: false,
  isConnected: false,
  isRecording: false,
  isTranscribing: false,
  currentTranscription: '',
  
  inputMode: 'text',
  
  error: null,
  
  isTyping: false,
  lastActivity: Date.now(),
}

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    // Session management
    setSession: (state, action: PayloadAction<InterviewSession>) => {
      state.session = action.payload
      state.error = null
    },
    
    updateSession: (state, action: PayloadAction<Partial<InterviewSession>>) => {
      if (state.session) {
        state.session = { ...state.session, ...action.payload }
      }
    },
    
    clearSession: (state) => {
      state.session = null
      state.messages = []
      state.progress = null
      state.scores = []
      state.error = null
    },
    
    // Messages
    addMessage: (state, action: PayloadAction<InterviewMessage>) => {
      state.messages.push(action.payload)
      state.lastActivity = Date.now()
    },
    
    setMessages: (state, action: PayloadAction<InterviewMessage[]>) => {
      state.messages = action.payload
    },
    
    // Progress tracking
    setProgress: (state, action: PayloadAction<InterviewProgress>) => {
      state.progress = action.payload
    },
    
    updateProgress: (state, action: PayloadAction<Partial<InterviewProgress>>) => {
      if (state.progress) {
        state.progress = { ...state.progress, ...action.payload }
      }
    },
    
    // Scores
    addScore: (state, action: PayloadAction<CompetencyScore>) => {
      const existingIndex = state.scores.findIndex(s => s.competency === action.payload.competency)
      if (existingIndex >= 0) {
        state.scores[existingIndex] = action.payload
      } else {
        state.scores.push(action.payload)
      }
    },
    
    setScores: (state, action: PayloadAction<CompetencyScore[]>) => {
      state.scores = action.payload
    },
    
    // Connection state
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload
      if (action.payload) {
        state.error = null
      }
    },
    
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    
    setRecording: (state, action: PayloadAction<boolean>) => {
      state.isRecording = action.payload
    },
    
    setTranscribing: (state, action: PayloadAction<boolean>) => {
      state.isTranscribing = action.payload
    },
    
    // Transcription
    setCurrentTranscription: (state, action: PayloadAction<string>) => {
      state.currentTranscription = action.payload
    },
    
    clearTranscription: (state) => {
      state.currentTranscription = ''
    },
    
    // Input mode
    setInputMode: (state, action: PayloadAction<'text' | 'voice'>) => {
      state.inputMode = action.payload
    },
    
    // Typing indicator
    setTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload
    },
    
    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    
    clearError: (state) => {
      state.error = null
    },
    
    // Update last activity
    updateLastActivity: (state) => {
      state.lastActivity = Date.now()
    },
    
    // End interview
    endInterview: (state, action: PayloadAction<{ reason?: string; finalMessage?: string }>) => {
      if (state.session) {
        state.session.status = 'completed'
        state.session.endTime = new Date().toISOString()
        state.session.interviewEndedByAi = true
        state.session.aiEndReason = action.payload.reason
        state.session.aiFinalMessage = action.payload.finalMessage
      }
      state.isRecording = false
      state.isTranscribing = false
    },
  },
})

export const {
  setSession,
  updateSession,
  clearSession,
  addMessage,
  setMessages,
  setProgress,
  updateProgress,
  addScore,
  setScores,
  setConnected,
  setLoading,
  setRecording,
  setTranscribing,
  setCurrentTranscription,
  clearTranscription,
  setInputMode,
  setTyping,
  setError,
  clearError,
  updateLastActivity,
  endInterview,
} = interviewSlice.actions

export default interviewSlice.reducer

// Selectors
export const selectInterview = (state: { interview: InterviewState }) => state.interview
export const selectSession = (state: { interview: InterviewState }) => state.interview.session
export const selectMessages = (state: { interview: InterviewState }) => state.interview.messages
export const selectProgress = (state: { interview: InterviewState }) => state.interview.progress
export const selectIsConnected = (state: { interview: InterviewState }) => state.interview.isConnected
export const selectIsRecording = (state: { interview: InterviewState }) => state.interview.isRecording
export const selectInputMode = (state: { interview: InterviewState }) => state.interview.inputMode
export const selectCurrentTranscription = (state: { interview: InterviewState }) => state.interview.currentTranscription